import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { digestTimes } from "@habitos/shared";
import { api } from "../lib/api";
import { useAuth, useSetUser } from "../lib/auth";
import { disablePush, enablePush, getPushState, isIos, isStandalone, type PushState } from "../lib/push";

const PUSH_TEXT: Record<PushState, string> = {
  unsupported: "Este navegador no permite notificaciones.",
  "needs-install": "En iPhone, los recordatorios solo funcionan con la app instalada en tu inicio.",
  "server-disabled": "El servidor todavía no tiene las notificaciones configuradas.",
  denied: "Bloqueaste las notificaciones. Actívalas en los ajustes del teléfono para esta app.",
  off: "Los recordatorios están apagados en este dispositivo.",
  on: "Los recordatorios están activos en este dispositivo.",
};

export function Settings() {
  const { user } = useAuth();
  const setUser = useSetUser();
  const [push, setPush] = useState<PushState | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const profile = useQuery({ queryKey: ["profile"], queryFn: api.profile });
  const times = profile.data ? digestTimes(profile.data) : null;

  useEffect(() => {
    getPushState().then(setPush, () => setPush("unsupported"));
  }, []);

  async function run(action: () => Promise<PushState>) {
    setMessage(null);
    try {
      setPush(await action());
    } catch (e) {
      setMessage((e as Error).message);
    }
  }

  async function test() {
    try {
      const { delivered } = await api.pushTest();
      setMessage(delivered > 0 ? "Enviamos una notificación de prueba." : "No hay dispositivos suscritos.");
    } catch (e) {
      setMessage((e as Error).message);
    }
  }

  return (
    <>
      <header className="page-header">
        <h1>Ajustes</h1>
      </header>

      <section className="card stack">
        <h2>Recordatorios</h2>
        <p>{push ? PUSH_TEXT[push] : "Revisando…"}</p>
        {push === "off" && <button className="btn primary" onClick={() => run(enablePush)}>Activar recordatorios</button>}
        {push === "on" && (
          <div className="row gap">
            <button className="btn" onClick={test}>Enviar prueba</button>
            <button className="btn" onClick={() => run(disablePush)}>Desactivar</button>
          </div>
        )}
        {message && <p className="muted">{message}</p>}
      </section>

      <section className="card stack">
        <h2>Avisos diarios</h2>
        <label className="toggle">
          <input
            type="checkbox"
            checked={user.morningSummary}
            onChange={async (e) => setUser(await api.updateMe({ morningSummary: e.target.checked }))}
          />
          Resumen de mi día por la mañana{times && ` (${times.morning})`}
        </label>
        <label className="toggle">
          <input
            type="checkbox"
            checked={user.eveningWrapUp}
            onChange={async (e) => setUser(await api.updateMe({ eveningWrapUp: e.target.checked }))}
          />
          Cierre del día si quedó algo sin marcar{times && ` (${times.evening})`}
        </label>
        <p className="muted small">
          La hora sale de cuándo te levantas y te duermes. Puedes cambiarla en <Link to="/perfil">tus respuestas</Link>.
        </p>
      </section>

      {isIos() && !isStandalone() && (
        <section className="card stack">
          <h2>Instalar en iPhone</h2>
          <ol className="steps">
            <li>Abre esta página en <b>Safari</b>.</li>
            <li>Toca el botón <b>Compartir</b> (el cuadro con la flecha hacia arriba).</li>
            <li>Elige <b>Agregar a inicio</b> y luego <b>Agregar</b>.</li>
            <li>Abre <b>Hábitos</b> desde el ícono nuevo y activa los recordatorios aquí.</li>
          </ol>
        </section>
      )}

      <section className="card stack">
        <h2>Tu asistente</h2>
        <p className="muted">Lo que respondiste al empezar le ayuda a armar planes a tu medida.</p>
        <div className="row gap">
          <Link to="/perfil" className="btn">Editar mis respuestas</Link>
          <Link to="/habitos" className="btn">Ver mis hábitos</Link>
        </div>
      </section>

      <section className="card stack">
        <h2>Tu cuenta</h2>
        <p>{user.name} · {user.email}</p>
        <p className="muted small">Zona horaria: {user.timezone}</p>
        {deviceZone !== user.timezone && (
          <button className="btn" onClick={async () => setUser(await api.updateMe({ timezone: deviceZone }))}>
            Usar la zona de este teléfono ({deviceZone})
          </button>
        )}
        <button
          className="btn danger"
          onClick={async () => {
            await api.logout();
            setUser(null);
          }}
        >
          Cerrar sesión
        </button>
      </section>
    </>
  );
}
